import ApplicationController from '@aha-app/mvc';
import DataStoreFields from '../collaboration/DataStoreFields';
import { type IDataStoreRecordBehaviors } from '../collaboration/DataStoreRecordBehaviors';
import { fieldFromJSON, type FieldType, type SerializedField } from '../fields';
import type BaseField from '../fields/BaseField';
import type SelectField from '../fields/SelectField';
import { type SelectOptionData } from '../fields/SelectOption';
import type SelectOption from '../fields/SelectOption';
import WhiteboardController from './WhiteboardController';

interface FieldsControllerState {
  fields: BaseField[];
}

// We're going to get rid of fields. See https://big.aha.io/develop/features/AWT-310
export const fieldsEnabled = () => false;

class FieldsController extends ApplicationController<FieldsControllerState> {
  whiteboardController: WhiteboardController;

  dataStore: DataStoreFields;

  startController() {
    this.whiteboardController =
      this.findControllerInstance(WhiteboardController)!;
    this.whiteboardController.fieldsController = this;

    this.dataStore = new DataStoreFields(
      this,
      this.whiteboardController.client
    );
    this.whiteboardController.dataStores.fields = this.dataStore;
  }

  get initialState() {
    return {
      fields: [],
    };
  }

  get statusField() {
    return this.dataStore.statusField;
  }

  addField(field: BaseField) {
    this.dataStore.addRecord(field);
    this.restoreField(field);
  }

  actionAddField(type: FieldType) {
    const field = fieldFromJSON({
      type,
      controller: this.whiteboardController,
      name: 'New field',
    });
    this.addField(field);
  }

  actionRemoveField(field: BaseField) {
    this.dataStore.removeRecord(field);
  }

  actionUpdateField(field: BaseField, attrs: Partial<SerializedField>) {
    let reRender = false;
    Object.entries(attrs).forEach(([key, value]) => {
      field.set(key, value);

      if (['hidden'].includes(key)) {
        reRender = true;
      }
    });

    if (reRender) {
      this.whiteboardController.canvas.requestRenderAll();
    }
  }

  actionUpdateSelectOption(
    field: SelectField,
    option: SelectOption,
    attrs: Partial<SelectOptionData>
  ) {
    field.options.update(option, attrs);
    this.whiteboardController.canvas.requestRenderAll();
  }

  actionRemoveSelectOption(field: SelectField, option: SelectOption) {
    field.options.remove(option.id);
    this.whiteboardController.canvas.requestRenderAll();
  }

  actionUpdateObjectsField(
    objects: IDataStoreRecordBehaviors[],
    field: BaseField,
    value: any
  ) {
    objects.forEach(object => field.setValueForObject(object, value));
    this.whiteboardController.canvas.requestRenderAll();
  }

  restoreField(field: BaseField) {
    this.state.fields.push(field);
  }

  removeField(field: BaseField) {
    const index = this.state.fields.findIndex(_field => _field.id === field.id);
    if (index >= 0) {
      this.state.fields.splice(index, 1);
    }
  }
}

export default FieldsController;
